import _mapValues from 'lodash/mapValues';
import _merge from 'lodash/merge';
import queryString from 'query-string';

import DeviceProps from '@magnus/react-native-device-props';
import UserProps from '@magnus/react-native-user-props';
import EvTruck from '@magnus/react-native-evtruck';

import Web2App from '@wowmaking/web2app';

import { disabled } from '@web-solutions/module-analytics';

export type AppLinks = {
  ADJUST_LINK?: string;
  ADJUST_TOKEN?: string;
  APPSFLYER_LINK?: string;
  ADJUST_LINK_ANDROID?: string;
  ADJUST_TOKEN_ANDROID?: string;
  WEB_APP_LINK?: string;
}

type URLBaseParamsType = {
  urlExtraPath?: string;
  query?: Record<string, string>
}

let IDFM: string | undefined;
let appLinks: AppLinks | undefined;
let rcTarget: string | undefined;

let URLBaseParams: URLBaseParamsType = {
  urlExtraPath: '',
  query: {}
};


export enum LinkTarget {
  DEFAULT = 'default', // empty - by default - go to AF/Adjust link and then to store
  WEB = 'web', // target is web app
}

/**
 * Facebook ad labels the funnel forwards to the backend so revenue can be tied
 * to a campaign/adset/creative in the cohort (ROMI) report. Field names and the
 * length limits are the backend contract — see the "Проброс рекламных меток
 * Facebook в бэкенд" brief.
 */
export type AdAttribution = {
  campaignId?: string;
  adsetId?: string;
  adId?: string;
  fbclid?: string;
  source?: string;
};

/**
 * Exported so the app's fresh-session storage wipe can carry it across: the ad
 * click happens once, and the labels must outlive a `localStorage.clear()` that
 * is only meant to reset the questionnaire.
 */
export const AD_ATTRIBUTION_STORAGE_KEY = 'ad_attribution';

/**
 * Three places hold the snapshot, read in this order: module memory,
 * localStorage, cookie.
 *
 * localStorage alone lost the labels for about half of the paying users.
 * Backend logs for 16.09 showed every unattributed purchase came from the
 * Facebook in-app browser on iOS (77% of purchases, ~65% of them unattributed;
 * zero from Safari/Chrome), on the same bundle, same landing URL with all params
 * still present at user creation, and the `session_url_params` event — read from
 * the very same `window.location.search` — carried every key. The only step
 * that could differ is the storage write/read, and that browser is known to
 * hand pages ephemeral or blocked WebKit storage while cookies keep working
 * (PostHog's cookie-backed identity survived those sessions intact).
 *
 * So: memory is the source of truth for this page load — it cannot be blocked
 * or cleared by anything — and the cookie is the persistence layer that still
 * works when localStorage does not. Both are written on capture.
 */
const AD_ATTRIBUTION_COOKIE = 'ad_attribution';
// 90 days is the ceiling, not the promise. It only holds in Chrome/Android. In
// WebKit — Safari and every iOS in-app browser, the FB one included — ITP caps a
// script-set cookie at 7 days, and at 24 hours when the visit arrived from a
// tracker domain with link decoration, which an `fbclid` in the URL is. That is
// fine: memory carries the page load where the user is created, and the cookie
// only needs to outlive a reload or a same-week return. FB's own click window
// is 7 days anyway, so nothing older would join to spend.
const AD_ATTRIBUTION_COOKIE_MAX_AGE = 60 * 60 * 24 * 90; // seconds

let adAttributionInMemory: AdAttribution | undefined;

type AdAttributionSource = 'memory' | 'localStorage' | 'cookie' | 'none';
let lastReadSource: AdAttributionSource = 'none';
// undefined until a capture actually tried to write.
let localStorageWriteOk: boolean | undefined;
// Whether THIS page load's URL carried any ad label — lets a later "nothing to
// send" be told apart: organic visit vs. labels seen and then lost.
let urlHadAdParams = false;

function readCookie(name: string): string | undefined {
  try {
    const prefix = `${name}=`;
    const hit = document.cookie.split('; ').find((c) => c.startsWith(prefix));

    return hit ? decodeURIComponent(hit.slice(prefix.length)) : undefined;
  } catch (_) {
    return undefined;
  }
}

function writeCookie(name: string, value: string): void {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';

    document.cookie =
      `${name}=${encodeURIComponent(value)}; Max-Age=${AD_ATTRIBUTION_COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  } catch (_) {
    // Cookies disabled — memory still carries this page load.
  }
}

function parseAdAttribution(raw: string | null | undefined): AdAttribution | undefined {
  if (!raw) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw);

    return parsed && typeof parsed === 'object' ? parsed as AdAttribution : undefined;
  } catch (_) {
    // Broken JSON — attribution is never worth an exception.
    return undefined;
  }
}

/**
 * [URL params to look under, payload field, max length the backend accepts].
 *
 * Two spellings per id on purpose: the brief specified `utm_campaign` /
 * `utm_adset` / `utm_ad`, but the live Ads Manager links are set up with
 * `campaign_id` / `adset_id` / `ad_id` — so reading only the brief's names left
 * every real buyer with no labels at all. First name that has a value wins.
 *
 * The first name of each row is also the canonical one: it is the key the
 * TrueGate payment metadata is written under, which the backend documented as
 * `utm_*` regardless of which spelling the ad link used.
 *
 * `campaign_name` / `adset_name` / `ad_name` / `ad_placement` / `ad_subnetwork`
 * are deliberately absent: the backend joins spend by id and does not take them.
 */
const AD_ATTRIBUTION_PARAMS: Array<[string[], keyof AdAttribution, number]> = [
  [['utm_campaign', 'campaign_id'], 'campaignId', 64],
  [['utm_adset', 'adset_id'], 'adsetId', 64],
  [['utm_ad', 'ad_id'], 'adId', 64],
  [['fbclid'], 'fbclid', 512],
  [['utm_source'], 'source', 64],
];

function readStoredAdAttribution(): AdAttribution | undefined {
  if (adAttributionInMemory) {
    lastReadSource = 'memory';
    return adAttributionInMemory;
  }

  let fromLocalStorage: AdAttribution | undefined;

  try {
    fromLocalStorage = parseAdAttribution(window.localStorage.getItem(AD_ATTRIBUTION_STORAGE_KEY));
  } catch (_) {
    // Storage access itself can throw (blocked storage) — fall through to the cookie.
  }

  if (fromLocalStorage) {
    lastReadSource = 'localStorage';
    adAttributionInMemory = fromLocalStorage;
    return fromLocalStorage;
  }

  const fromCookie = parseAdAttribution(readCookie(AD_ATTRIBUTION_COOKIE));

  if (fromCookie) {
    lastReadSource = 'cookie';
    adAttributionInMemory = fromCookie;
    return fromCookie;
  }

  lastReadSource = 'none';
  return undefined;
}

/**
 * Snapshot the FB ad labels from the current URL into localStorage.
 *
 * Only writes when the URL actually carries at least one of them, so a reload,
 * an in-funnel navigation or a later organic visit never wipes what the ad click
 * brought in. Nothing is invented: a param that isn't there produces no field.
 *
 * An over-long value is dropped, not truncated — the backend drops it anyway,
 * and a cut id is a *wrong* id that would silently join to the wrong campaign.
 */
function captureAdAttribution(): void {
  try {
    const params = queryString.parse(window.location.search);

    const snapshot: AdAttribution = {};

    AD_ATTRIBUTION_PARAMS.forEach(([names, field, maxLength]) => {
      for (const name of names) {
        const raw = params[name];
        const value = Array.isArray(raw) ? raw[0] : raw;

        if (typeof value === 'string' && value && value.length <= maxLength) {
          snapshot[field] = value;
          break;
        }
      }
    });

    if (!Object.keys(snapshot).length) {
      return;
    }

    urlHadAdParams = true;

    // Memory first — nothing can block or wipe it for the rest of this page
    // load, and user creation happens within it.
    adAttributionInMemory = snapshot;

    const serialized = JSON.stringify(snapshot);

    try {
      window.localStorage.setItem(AD_ATTRIBUTION_STORAGE_KEY, serialized);
      // A write that "succeeds" but does not read back is a blocked store too.
      localStorageWriteOk = window.localStorage.getItem(AD_ATTRIBUTION_STORAGE_KEY) === serialized;
    } catch (_) {
      localStorageWriteOk = false;
    }

    writeCookie(AD_ATTRIBUTION_COOKIE, serialized);
  } catch (_) {
    // The funnel must keep working whatever the browser does to storage.
  }
}


const AttributionModule = {
  init(links: AppLinks) {
    // Must run on every landing, before anything else here: the labels only
    // exist in the URL of the very first screen, and the purchase happens many
    // screens (and reloads) later.
    captureAdAttribution();

    appLinks = links;

    DeviceProps.getIDFM().then((idfm) => {
      IDFM = idfm;
    });

    const params = queryString.parse(window.location.search);

    const attribution = _mapValues({
      utm_source: params.utm_source || '',
      campaign_name: params.campaign_name || '',
      campaign_id: params.campaign_id || '',
      adset_name: params.adset_name || '',
      adset_id: params.adset_id || '',
      ad_name: params.ad_name || '',
      ad_id: params.ad_id || '',
      http_referer: window.document.referrer || '',
      ad_subnetwork: params.ad_subnetwork || '',
      ad_keyword: params.ad_keyword || '',
      ad_placement: params.ad_placement || '',
    }, String);

    if (!disabled) {
      EvTruck.trackWebAttribution(attribution);

      Web2App.init({
        onChange: (system, p) => {
          // оборачиваем в массив, в будущем сможем поддерживать несколько пикселей на сайте и тп
          EvTruck.trackWebMeasurements({
            external_id: Web2App.getAttribution().web_external_id,
            [system]: [p],
          });
        },
      });

      if (appLinks.ADJUST_LINK && appLinks.ADJUST_TOKEN) {
        return Web2App.trackAdjustImpression(appLinks.ADJUST_TOKEN);
      }

      if (appLinks.ADJUST_LINK_ANDROID && appLinks.ADJUST_TOKEN_ANDROID) {
        return Web2App.trackAdjustImpression(appLinks.ADJUST_TOKEN_ANDROID);
      }
    }
  },

  captureAdAttribution,

  /**
   * The stored FB ad labels, ready to be put on the top level of an API request
   * body as `attribution`. `undefined` for an organic visit — the object is then
   * omitted from the request entirely rather than sent empty.
   *
   * The backend keeps the FIRST snapshot it ever receives for a user and
   * silently ignores every later one, so sending this on every request is safe
   * and deliberate — no deduplication is needed here.
   */
  getAdAttribution(): AdAttribution | undefined {
    const stored = readStoredAdAttribution();

    if (!stored) {
      return undefined;
    }

    const attribution: AdAttribution = {};

    AD_ATTRIBUTION_PARAMS.forEach(([, field]) => {
      const value = stored[field];

      if (typeof value === 'string' && value) {
        attribution[field] = value;
      }
    });

    // A snapshot carrying neither campaignId nor adId joins to nothing on the
    // spend side, and the backend discards it — but it would still burn the
    // user's one first-touch slot forever, so an fbclid/source-only snapshot
    // must never be sent.
    if (!attribution.campaignId && !attribution.adId) {
      return undefined;
    }

    return attribution;
  },

  /**
   * One-line health check for analytics, so a missing-attribution session can
   * be explained from the data instead of guessed at: whether localStorage
   * accepted the write, where the labels were read from, and whether there are
   * any. Every value is a string — an `undefined` property never enters the
   * PostHog taxonomy and reads as "never implemented".
   */
  getAdAttributionDiag(): {
    storage: 'ok' | 'blocked' | 'untested';
    source: AdAttributionSource;
    labels: 'yes' | 'no';
    urlParams: 'yes' | 'no';
  } {
    const labels = AttributionModule.getAdAttribution() ? 'yes' : 'no';

    return {
      storage: localStorageWriteOk === undefined ? 'untested' : localStorageWriteOk ? 'ok' : 'blocked',
      source: lastReadSource,
      labels,
      urlParams: urlHadAdParams ? 'yes' : 'no',
    };
  },

  /**
   * The same stored labels under their ORIGINAL URL names, for the channels that
   * pass them through verbatim instead of using the billing API's field names —
   * currently the TrueGate payment metadata, which the payment webhook reads back.
   *
   * `{}` for an organic visit, and a param that was never in the URL produces no
   * key at all.
   */
  getAdAttributionUrlParams(): Record<string, string> {
    const stored = readStoredAdAttribution();

    if (!stored) {
      return {};
    }

    const params: Record<string, string> = {};

    AD_ATTRIBUTION_PARAMS.forEach(([names, field]) => {
      const value = stored[field];

      if (typeof value === 'string' && value) {
        // Canonical name, not whichever spelling the ad link happened to use —
        // the backend reads the metadata under the `utm_*` keys.
        params[names[0]] = value;
      }
    });

    return params;
  },

  setURLBaseParams(params: URLBaseParamsType) {
    URLBaseParams = _merge(URLBaseParams, params)
  },

  setRemoteConfigTarget(target: string) {
    rcTarget = target;
  },

  getMailLinkTarget() {
    return appLinks?.WEB_APP_LINK && rcTarget === LinkTarget.WEB ? LinkTarget.WEB : undefined;
  },

  async getLinkToApp(linkTargetExtra?: LinkTarget) {
    const source = 'sub';
    const params = queryString.parse(window.location.search);

    if (!appLinks) {
      return Promise.reject();
    }

    const up = await UserProps.fetch() as { utm_source: string, campaign_id: string, target: string, };
    const linkTarget = linkTargetExtra || up.target;
    const rcTargetExtra = linkTargetExtra || rcTarget;

    const w = appLinks.WEB_APP_LINK
      ? Promise.resolve(
        queryString.stringifyUrl({
          url: `${appLinks.WEB_APP_LINK}${URLBaseParams.urlExtraPath}`,
          query: {
            idfm: IDFM,
            source,
            mode: params.mode,
            utm_source: up.utm_source,
            campaign_id: up.campaign_id,
            ...URLBaseParams.query
          },
        })
      )
      : null;

    if ((linkTarget === LinkTarget.WEB || rcTargetExtra === LinkTarget.WEB) && w) {
      return w;
    } else if (appLinks.APPSFLYER_LINK) {
      return Web2App.createAppsFlyerLink(appLinks.APPSFLYER_LINK, { uuid: IDFM, is_retargeting: true, source });
    } else if (appLinks.ADJUST_LINK && appLinks.ADJUST_TOKEN) {
      const adj_redirect_android =
        appLinks.ADJUST_LINK_ANDROID && appLinks.ADJUST_TOKEN_ANDROID
          ? Web2App.createAdjustLink(appLinks.ADJUST_LINK_ANDROID, appLinks.ADJUST_TOKEN_ANDROID, {
            uuid: IDFM,
            source,
            mode: params.mode,
          })
          : undefined;

      return Web2App.createAdjustLink(appLinks.ADJUST_LINK, appLinks.ADJUST_TOKEN, {
        uuid: IDFM,
        source,
        mode: params.mode,
        adj_redirect_android,
      });

    } else {
      return Promise.reject(Error('Cannot create app link'));
    }
  },
};

export default AttributionModule;
